<template>
  <div>
    <div class="container">
      <!-- Header -->
      <div class="header">
        <h1 class="header-title">
          <Wrench class="header-icon" />
          メインテナンススケジュール
        </h1>
        <div class="header-actions">
          <button class="primary-btn" @click="showForm = true">
            <Plus class="btn-icon" />
            新しいメインテナンス
          </button>
        </div>
        <p class="header-subtitle">Track and monitor system maintenance activities</p>
      </div>

      <!-- Filter Buttons -->
      <div class="filter-buttons">
        <button
          v-for="status in statusList"
          :key="status"
          @click="filter = status"
          :class="['filter-btn', { active: filter === status }]"
        >
          {{ getStatusLabel(status) }}
          <span class="filter-count">
            ({{ status === 'all'
              ? schedules.length
              : schedules.filter(s => s.status === status).length
            }})
          </span>
        </button>
      </div>

      <!-- Loading Spinner -->
      <div
        v-if="loading"
        class="loading-container"
        style="position:fixed; left:0; top:0; width:100vw; height:100vh; z-index:50;"
      >
        <div class="loading-content">
          <div class="spinner"></div>
          <p class="header-subtitle">Loading maintenance schedules...</p>
        </div>
      </div>

      <!-- Create Maintenance Modal -->
      <div v-if="showForm" class="modal-backdrop">
        <div class="modal">
          <div class="modal-header">
            <h3>Create maintenance event</h3>
            <button class="icon-btn" @click="closeForm"><X /></button>
          </div>

          <form @submit.prevent="submitNewEvent" class="form-grid">
            <div class="form-row">
              <label>メインテナンスタイトル</label>
              <input v-model.trim="newEvent.kind_of_maintenance" type="text" required />
            </div>

            <div class="form-row">
              <label>優先度</label>
              <select v-model="newEvent.priority" required>
                <option value="critical">最重要</option>
                <option value="high">高</option>
                <option value="medium">中</option>
                <option value="low">低</option>
              </select>
            </div>

            <div class="form-row">
              <label>ステータス</label>
              <select v-model="newEvent.status" required>
                <option value="scheduled">予定</option>
                <option value="in_progress">進行中</option>
                <option value="completed">完了</option>
              </select>
            </div>

            <div class="form-row">
              <label>開始予定時刻</label>
              <input v-model="newEvent.starting_from" type="datetime-local" required />
            </div>

            <div class="form-row">
              <label>終了予定時刻</label>
              <input v-model="newEvent.estimated_completion" type="datetime-local" :min="newEvent.starting_from" required />
            </div>

            <div class="form-row form-row-col">
              <label>予想される変化</label>
              <textarea v-model.trim="newEvent.expected_changes" rows="3" required></textarea>
            </div>

            <div class="modal-actions">
              <button type="button" class="secondary-btn" @click="closeForm">Cancel</button>
              <button type="submit" class="primary-btn">
                <Save class="btn-icon" />
                Save
              </button>
            </div>

            <p v-if="formError" class="form-error">{{ formError }}</p>
          </form>
        </div>
      </div>


      <!-- Maintenance Cards -->
      <div v-else class="cards-grid">
        <div
          v-for="schedule in filteredSchedules"
          :key="schedule.id"
          class="maintenance-card"
        >
          <div class="card-content">
            <div class="card-header">
              <h3 class="card-title">
                {{ schedule.kind_of_maintenance }}
              </h3>
              <div class="" style="display: flex;">
                <div class="card-badges">
                  <span
                    :class="['priority-badge', 
                      schedule.priority === 'critical' ? 'priority-critical' : '',
                      schedule.priority === 'high' ? 'priority-high' : '',
                      schedule.priority === 'medium' ? 'priority-medium' : '',
                      schedule.priority === 'low' ? 'priority-low' : ''
                    ]"
                  >
                    {{ getPriorityLabel(schedule.priority) }}
                  </span>
                  <div
                    :class="[
                      'status-badge',
                      schedule.status === 'scheduled' ? 'status-scheduled' : '',
                      schedule.status === 'in_progress' ? 'status-in-progress' : '',
                      schedule.status === 'completed' ? 'status-completed' : ''
                    ]"
                  >
                    <component :is="getStatusIcon(getStatusLabel(null, schedule))" class="status-icon" />
                    <span class="status-text">{{ getStatusLabel(null, schedule) }}</span>
                  </div>
                </div>
                <button
                  v-if="schedule.status !== 'completed'"
                  class="secondary-btn secondary-btn--sm"
                  style="margin-left: auto;"
                  @click="openEdit(schedule)"
                  title="Edit maintenance"
                >
                  <Pencil class="btn-icon" />
                  変更する
                </button>
                <button
                  v-if="schedule.status !== 'completed'"
                  class="danger-btn danger-btn--sm"
                  @click="openDelete(schedule)"
                  title="Delete maintenance"
                >
                  <Trash class="btn-icon" />
                </button>
              </div>
            </div>

            <div class="datetime-section">
              <div class="datetime-item">
                <Calendar class="datetime-icon" />
                <div class="datetime-content">
                  <div class="datetime-label">開始予定時刻： {{ formatDateTime(schedule.starting_from) }}</div>
                  <div>終了予定時刻： {{ formatDateTime(schedule.estimated_completion) }}</div>
                </div>
              </div>
            </div>

            <div class="changes-section">
              <h4 class="changes-title">予想される変更：</h4>
              <p class="changes-text">
                {{ schedule.expected_changes }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit Maintenance Modal -->
      <div v-if="showEdit" class="modal-backdrop">
        <div class="modal">
          <div class="modal-header">
            <h3>メインテナンスを編集する</h3>
            <button class="icon-btn" @click="closeEdit"><X /></button>
          </div>

          <form @submit.prevent="submitEditEvent" class="form-grid">
            <div class="form-row">
              <label>メインテナンスタイトル</label>
              <input v-model.trim="editEvent.kind_of_maintenance" type="text" required />
            </div>

            <div class="form-row">
              <label>優先度</label>
              <select v-model="editEvent.priority" required>
                <option value="critical">最重要</option>
                <option value="high">高</option>
                <option value="medium">中</option>
                <option value="low">低</option>
              </select>
            </div>

            <div class="form-row">
              <label>ステータス</label>
              <select v-model="editEvent.status" required>
                <option value="scheduled">予定</option>
                <option value="in_progress">進行中</option>
                <option value="completed">完了</option>
              </select>
            </div>

            <div class="form-row">
              <label>開始予定時刻</label>
              <input v-model="editEvent.starting_from" type="datetime-local" required />
            </div>

            <div class="form-row">
              <label>終了予定時刻</label>
              <input
                v-model="editEvent.estimated_completion"
                type="datetime-local"
                :min="editEvent.starting_from"
                required
              />
            </div>

            <div class="form-row form-row-col">
              <label>予想される変化</label>
              <textarea v-model.trim="editEvent.expected_changes" rows="3" required></textarea>
            </div>

            <div class="modal-actions">
              <button type="button" class="secondary-btn" @click="closeEdit">Cancel</button>
              <button type="submit" class="primary-btn">
                <Save class="btn-icon" />
                保存
              </button>
            </div>

            <p v-if="editError" class="form-error">{{ editError }}</p>
          </form>
        </div>
      </div>

      <!-- Delete Maintenance Modal -->
      <div v-if="showDelete" class="modal-backdrop">
        <div class="modal">
          <div class="modal-header">
            <h3>Delete maintenance event</h3>
            <button class="icon-btn" @click="closeDelete"><X /></button>
          </div>

          <div class="form-grid">
            <div style="align-items: center;">
              <AlertTriangle class="datetime-icon" />
            </div>
            <p class="changes-text" style="margin: .25rem 0 .5rem;">
              このメンテナンスを削除しますか？この操作は取り消せません。
            </p>
            <div class="datetime-section">
              <div class="datetime-item">
                
                <div class="datetime-content">
                  <div class="datetime-label">{{ deletingPreview.kind_of_maintenance }}</div>
                  <div>Start: {{ formatDateTime(deletingPreview.starting_from) }}</div>
                  <div>End: {{ formatDateTime(deletingPreview.estimated_completion) }}</div>
                </div>
              </div>
            </div>

            <p v-if="deleteError" class="form-error">{{ deleteError }}</p>

            <div class="modal-actions">
              <button type="button" class="secondary-btn" @click="closeDelete">Cancel</button>
              <button type="button" class="danger-btn" @click="confirmDelete" :disabled="deleting">
                <Trash class="btn-icon" />
                {{ deleting ? 'Deleting…' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- No schedules found -->
      <div v-if="!loading && filteredSchedules.length === 0" class="empty-state">
        <XCircle class="empty-icon" />
        <h3 class="empty-title">No maintenance schedules found</h3>
        <p class="empty-text">No schedules match the current filter criteria.</p>
      </div>

      <!-- Summary Stats -->
      <div class="summary-card">
        <h3 class="summary-title">まとめ</h3>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-number summary-scheduled">
              {{ schedules.filter(s => s.status === 'scheduled').length }}
            </div>
            <div class="summary-label">予定</div>
          </div>
          <div class="summary-item">
            <div class="summary-number summary-in-progress">
              {{ schedules.filter(s => s.status === 'in_progress').length }}
            </div>
            <div class="summary-label">進行中</div>
          </div>
          <div class="summary-item">
            <div class="summary-number summary-completed">
              {{ schedules.filter(s => s.status === 'completed').length }}
            </div>
            <div class="summary-label">完了</div>
          </div>
          <div class="summary-item">
            <div class="summary-number summary-total">
              {{ schedules.length }}
            </div>
            <div class="summary-label">合計</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Calendar, Clock, Wrench, AlertCircle, CheckCircle, XCircle, Plus, Save, X, Pencil, Trash, AlertTriangle } from 'lucide-vue-next';
import axios from 'axios';

const schedules = ref([]);
const loading = ref(true);
const filter = ref('all');

const statusList = ['all', 'scheduled', 'in_progress', 'completed'];

onMounted(async () => {
  loading.value = true;
  schedules.value = [];
  schedules.value = await GetMaintentanceData();
  console.log('maintenance result', schedules.value);
  
  loading.value = false;
});

const GetMaintentanceData = async ()=> {
  const param = {
    key: 'fi3h279h870fye872Y5348YTG8475YG&*43287FYRG'
  };

  const result = await axios.post(`${process.env.VUE_APP_BACKEND_URL}maintenance/schedule`, param)
  .then(response => {
    return response.data;
  })
  .catch(error => {
    console.error('Error when sending GetMaintentanceData', error);
  })

  return result;
};

const formatDateTime = dateString => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusIcon = status => {
  console.log('get status icon', status);
  switch (status) {
    case 'completed':
    case '完了':
      return CheckCircle;
    case 'in_progress':
    case '進行中':
      return Clock;
    case 'scheduled':
    case '予定':
      return Calendar;
    default:
      return AlertCircle;
  }
};

const getPriorityLabel = priority => {
  switch (priority)
  {
    case 'low':
      return "低";
    case 'medium':
      return "中";
    case 'high':
      return "高";
    case 'critical':
      return "最重要";
  }
};

const getStatusLabel = (status, event = null) => {
  switch (status)
  {
    case 'scheduled': return '予定';
    case 'in_progress': return '進行中';
    case 'completed': return '完了';
    case 'all': return '全部';
  }

  const now = new Date();
  const start = new Date(event.starting_from);
  const end = new Date(event.estimated_completion);

  let effectiveStatus;

  if (now < start) {
    effectiveStatus = 'scheduled';
  } else if (now >= start && now < end) {
    effectiveStatus = 'in_progress';
  } else {
    effectiveStatus = 'completed';
  }

  switch (effectiveStatus) {
    case 'scheduled': return '予定';
    case 'in_progress': return '進行中';
    case 'completed': return '完了';
    case 'all': return '全部'; // still keep this for other logic
    default: return '';
  }
};

const filteredSchedules = computed(() => {
  if (filter.value === 'all') return schedules.value;
  return schedules.value.filter(schedule => schedule.status === filter.value);
});

const showForm = ref(false);
const formError = ref('');
const submitting = ref(false);

const newEventDefault = () => ({
  kind_of_maintenance: '',
  priority: 'medium',
  status: 'scheduled',
  starting_from: '',
  estimated_completion: '',
  expected_changes: ''
});
const newEvent = ref(newEventDefault());

const closeForm = () => {
  showForm.value = false;
  formError.value = '';
  newEvent.value = newEventDefault();
};

const submitNewEvent = async () => {
  formError.value = '';

  // Basic front-end validation
  if (!newEvent.value.kind_of_maintenance ||
      !newEvent.value.priority ||
      !newEvent.value.status ||
      !newEvent.value.starting_from ||
      !newEvent.value.estimated_completion ||
      !newEvent.value.expected_changes) {
    formError.value = 'すべてのフィールドに入力してください。';
    return;
  }

  const start = new Date(newEvent.value.starting_from);
  const end = new Date(newEvent.value.estimated_completion);
  if (isNaN(start) || isNaN(end) || end < start) {
    formError.value = 'Estimated end must be after start.';
    return;
  }

  submitting.value = true;

  try {
    // Adjust the endpoint/shape to match your API.
    const payload = {
      key: 'fi3h279h870fye872Y5348YTG8475YG&*43287FYRG',
      ...newEvent.value
    };

    const { data } = await axios.post(
      `${process.env.VUE_APP_BACKEND_URL}maintenance/schedule/create`,
      payload
    );

    // If API returns the created record: use it; else build one locally.
    const created = data?.id ? data : {
      id: Date.now(), // fallback temporary id
      ...newEvent.value
    };

    // Add to list (top) for instant feedback
    schedules.value = [created, ...schedules.value];

    closeForm();
  } catch (err) {
    console.error('Error creating maintenance event', err);
    formError.value = 'Failed to create event. Please try again.';
  } finally {
    submitting.value = false;
  }
};

// --- Edit modal state ---
const showEdit = ref(false);
const editError = ref('');
const editingId = ref(null);
const editEvent = ref({
  kind_of_maintenance: '',
  priority: 'medium',
  status: 'scheduled',
  starting_from: '',
  estimated_completion: '',
  expected_changes: ''
});

// Convert ISO → datetime-local string (YYYY-MM-DDTHH:mm) respecting local TZ
const toLocalInput = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return '';
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

// Convert datetime-local string → ISO
// const toISO = (localStr) => {
//   const d = new Date(localStr);
//   return isNaN(d) ? '' : d.toISOString();
// };

const openEdit = (schedule) => {
  if (schedule.status === 'completed') return; // guard (shouldn't happen due to v-if)
  editError.value = '';
  editingId.value = schedule.id;

  // Prefill fields (clone + normalize datetime-local values)
  editEvent.value = {
    kind_of_maintenance: schedule.kind_of_maintenance ?? '',
    priority: schedule.priority ?? 'medium',
    status: schedule.status ?? 'scheduled',
    starting_from: toLocalInput(schedule.starting_from),
    estimated_completion: toLocalInput(schedule.estimated_completion),
    expected_changes: schedule.expected_changes ?? ''
  };

  showEdit.value = true;
};

const closeEdit = () => {
  showEdit.value = false;
  editError.value = '';
  editingId.value = null;
  // reset contents (optional)
};

const toServerDatetime = (localStr) => {
  // localStr comes from <input type="datetime-local"> as "YYYY-MM-DDTHH:mm"
  // Ensure it's trimmed to minutes and no timezone suffix.
  return (localStr || '').slice(0, 16);
};

const submitEditEvent = async () => {
  editError.value = '';

  // Validate
  const e = editEvent.value;
  if (!e.kind_of_maintenance || !e.priority || !e.status || !e.starting_from || !e.estimated_completion || !e.expected_changes) {
    editError.value = 'すべてのフィールドに入力してください。';
    return;
  }
  const start = new Date(e.starting_from);
  const end = new Date(e.estimated_completion);
  if (isNaN(start) || isNaN(end) || end < start) {
    editError.value = 'Estimated end must be after start.';
    return;
  }

  try {
    // Build payload; convert datetime-local → ISO for backend
    const payload = {
      key: 'fi3h279h870fye872Y5348YTG8475YG&*43287FYRG',
      kind_of_maintenance: e.kind_of_maintenance,
      priority: e.priority,
      status: e.status,
      starting_from: toServerDatetime(e.starting_from),
      estimated_completion: toServerDatetime(e.estimated_completion),
      expected_changes: e.expected_changes,
      id: editingId.value
    };

    // Adjust URL/shape to your API
    const url = `${process.env.VUE_APP_BACKEND_URL}maintenance/schedule/update`;
    const { data } = await axios.post(url, payload);

    // Prefer server response; otherwise merge local
    const updated = data?.id ? data : { id: editingId.value, ...payload };

    // Optimistically update in-place
    const idx = schedules.value.findIndex(s => s.id === editingId.value);
    if (idx !== -1) {
      // Keep whatever fields your cards use
      schedules.value[idx] = {
        ...schedules.value[idx],
        ...updated
      };
      // Force reactivity if needed
      schedules.value = [...schedules.value];
    }

    closeEdit();
  } catch (err) {
    console.error('Error updating maintenance event', err);
    editError.value = 'Failed to update event. Please try again.';
  }
};

// --- Delete state ---
const showDelete = ref(false);
const deleting = ref(false);
const deletingId = ref(null);
const deletingPreview = ref({
  kind_of_maintenance: '',
  starting_from: '',
  estimated_completion: ''
});
const deleteError = ref('');

const openDelete = (schedule) => {
  if (schedule.status === 'completed') return; // keep consistent with Edit
  deleteError.value = '';
  deletingId.value = schedule.id;

  // Prepare preview values (accept camelCase or snake_case just in case)
  deletingPreview.value = {
    kind_of_maintenance: schedule.kind_of_maintenance ?? '',
    starting_from: schedule.starting_from ?? '',
    estimated_completion: schedule.estimated_completion ?? ''
  };

  showDelete.value = true;
};

const closeDelete = () => {
  showDelete.value = false;
  deleteError.value = '';
  deletingId.value = null;
};

const confirmDelete = async () => {
  if (!deletingId.value) return;
  deleting.value = true;
  deleteError.value = '';

  try {
    const url = `${process.env.VUE_APP_BACKEND_URL}maintenance/schedule/delete`;
    const payload = {
      key: 'fi3h279h870fye872Y5348YTG8475YG&*43287FYRG',
      id: deletingId.value
    };

    const { data } = await axios.post(url, payload);
    console.log('delete result', data);
    // If server returns success flag, you can check it; otherwise just remove locally
    schedules.value = schedules.value.filter(s => s.id !== deletingId.value);

    closeDelete();
  } catch (err) {
    console.error('Error deleting maintenance event', err);
    deleteError.value = 'Failed to delete event. Please try again.';
  } finally {
    deleting.value = false;
  }
};
</script>

<style>
    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        background-color: #f9fafb;
        color: #374151;
        line-height: 1.6;
    }

    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 2rem 1rem;
    }

    .header {
      margin-bottom: 2rem;
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: end;
      gap: 0.75rem;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .header-title {
        display: flex;
        align-items: center;
        font-size: 2rem;
        font-weight: bold;
        color: #111827;
        margin-bottom: 0.5rem;
    }

    .header-icon {
        width: 2rem;
        height: 2rem;
        margin-right: 0.75rem;
        color: #2563eb;
    }

    .header-subtitle {
        color: #6b7280;
    }

    .loading-container {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f9fafb;
    }

    .loading-content {
        text-align: center;
    }

    .spinner {
        width: 3rem;
        height: 3rem;
        border: 2px solid #e5e7eb;
        border-top: 2px solid #2563eb;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 1rem;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .filter-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
    }

    .filter-btn {
        padding: 0.5rem 1rem;
        border-radius: 0.5rem;
        font-weight: 500;
        border: 1px solid #d1d5db;
        background-color: white;
        color: #374151;
        cursor: pointer;
        transition: all 0.2s;
    }

    .filter-btn:hover {
        background-color: #f3f4f6;
    }

    .filter-btn.active {
        background-color: #2563eb;
        color: white;
        border-color: #2563eb;
    }

    .filter-count {
        margin-left: 0.5rem;
        font-size: 0.875rem;
    }

    .cards-grid {
        display: grid;
        gap: 1.5rem;
        grid-template-columns: 1fr;
    }

    @media (min-width: 768px) {
        .cards-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (min-width: 1024px) {
        .cards-grid {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    .maintenance-card {
        background: white;
        border-radius: 0.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
        overflow: hidden;
    }

    .card-content {
        padding: 1.5rem;
    }

    .card-header {
        /*display: flex;*/
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1rem;
    }

    .card-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
        line-height: 1.4;
        flex: 1;
        margin-right: 1rem;
    }

    .card-badges {
        display: flex;
        /*flex-direction: column;*/
        align-items: flex-end;
        gap: 0.5rem;
    }

    .priority-badge {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
        font-weight: 500;
        border-radius: 9999px;
    }

    .priority-critical {
        background-color: #fef2f2;
        color: #991b1b;
    }

    .priority-high {
        background-color: #fff7ed;
        color: #c2410c;
    }

    .priority-medium {
        background-color: #fefce8;
        color: #a16207;
    }

    .priority-low {
        background-color: #f0fdf4;
        color: #166534;
    }

    .status-badge {
        display: flex;
        align-items: center;
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
        font-weight: 500;
        border-radius: 9999px;
        border: 1px solid;
    }

    .status-scheduled {
        background-color: #eff6ff;
        color: #1d4ed8;
        border-color: #bfdbfe;
    }

    .status-in-progress {
        background-color: #fefce8;
        color: #a16207;
        border-color: #fde68a;
    }

    .status-completed {
        background-color: #f0fdf4;
        color: #166534;
        border-color: #bbf7d0;
    }

    .status-icon {
        width: 1rem;
        height: 1rem;
        margin-right: 0.25rem;
    }

    .datetime-section {
        margin-bottom: 1rem;
    }

    .datetime-item {
        display: flex;
        align-items: flex-start;
        font-size: 0.875rem;
        color: #6b7280;
    }

    .datetime-icon {
        width: 1rem;
        height: 1rem;
        margin-right: 0.5rem;
        margin-top: 0.125rem;
        flex-shrink: 0;
    }

    .datetime-content {
        flex: 1;
    }

    .datetime-label {
        font-weight: 500;
    }

    .changes-section {
        border-top: 1px solid #e5e7eb;
        padding-top: 1rem;
    }

    .changes-title {
        font-size: 0.875rem;
        font-weight: 500;
        color: #111827;
        margin-bottom: 0.5rem;
    }

    .changes-text {
        font-size: 0.875rem;
        color: #6b7280;
        line-height: 1.5;
    }

    .empty-state {
        text-align: center;
        padding: 3rem 0;
    }

    .empty-icon {
        width: 3rem;
        height: 3rem;
        color: #9ca3af;
        margin: 0 auto 1rem;
    }

    .empty-title {
        font-size: 1.125rem;
        font-weight: 500;
        color: #111827;
        margin-bottom: 0.5rem;
    }

    .empty-text {
        color: #6b7280;
    }

    .summary-card {
        background: white;
        border-radius: 0.5rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
        padding: 1.5rem;
        margin-top: 2rem;
    }

    .summary-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 1rem;
    }

    .summary-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
    }

    @media (min-width: 768px) {
        .summary-grid {
            grid-template-columns: repeat(4, 1fr);
        }
    }

    .summary-item {
        text-align: center;
    }

    .summary-number {
        font-size: 2rem;
        font-weight: bold;
        margin-bottom: 0.25rem;
    }

    .summary-scheduled { color: #2563eb; }
    .summary-in-progress { color: #d97706; }
    .summary-completed { color: #059669; }
    .summary-total { color: #111827; }

    .summary-label {
        font-size: 0.875rem;
        color: #6b7280;
    }

    .status-text {
        text-transform: capitalize;
    }

    .primary-btn,
    .secondary-btn,
    .icon-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      border-radius: 0.5rem;
      padding: 0.5rem 0.9rem;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid transparent;
    }

    .primary-btn {
      background: #2563eb;
      color: #fff;
    }
    .primary-btn:hover { background: #1e40af; }

    .secondary-btn {
      background: #fff;
      color: #374151;
      border-color: #d1d5db;
    }
    .secondary-btn:hover { background: #f3f4f6; }

    .icon-btn {
      background: transparent;
      color: #6b7280;
      padding: 0.25rem;
    }
    .icon-btn:hover { color: #111827; }

    .btn-icon {
      width: 1rem;
      height: 1rem;
    }

    /* Modal */
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(17, 24, 39, 0.5);
      display: grid;
      place-items: center;
      z-index: 60;
      padding: 1rem;
    }
    .modal {
      width: 100%;
      max-width: 640px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 0.75rem;
      box-shadow: 0 10px 30px rgba(0,0,0,0.15);
      padding: 1rem;
    }
    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: .5rem;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 1rem;
    }
    .form-grid {
      display: grid;
      gap: 0.75rem;
    }
    .form-row {
      display: grid;
      gap: 0.25rem;
    }
    .form-row-col textarea,
    .form-row input,
    .form-row select {
      width: 100%;
      border: 1px solid #d1d5db;
      border-radius: 0.5rem;
      padding: 0.5rem 0.65rem;
      font-size: 0.95rem;
    }
    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 0.75rem;
    }
    .form-error {
      color: #b91c1c;
      margin-top: 0.25rem;
    }

    .card-badges-actions {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem;
    }

    /* Small variant for secondary button */
    .secondary-btn--sm {
      padding: 0.35rem 0.6rem;
      font-size: 0.85rem;
    }

    .danger-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      border-radius: 0.5rem;
      padding: 0.5rem 0.9rem;
      font-weight: 600;
      cursor: pointer;
      color: #fff;
      background: #dc2626;         /* red-600 */
      border: 1px solid #dc2626;
      margin-left: 10px;
    }
    .danger-btn:hover { background: #b91c1c; border-color: #b91c1c; }

    .danger-btn--sm {
      padding: 0.35rem 0.6rem;
      font-size: 0.85rem;
    }
</style>